import { isNotOnPanelError } from "features/cdm/utils/panelTransfer";
import { isDateInCurrentMonth } from "features/cdm/utils/registrationTiming";

// When enabled, a failing/absent PCRS check no longer blocks CDM — the user may
// still start and work on reviews (status/notices are still shown). Defaults to
// off, so PCRS problems block as before.
const ALLOW_REVIEW_ON_PCRS_ERROR =
  process.env.REACT_APP_CDM_ALLOW_REVIEW_ON_PCRS_ERROR === "true";

const NON_ENROLLED_PROGRAMMES = new Set(["NOT_ENROLLED", "DEREGISTERED"]);

export const isPatientEnrolled = (currentProgramme) =>
  !!currentProgramme && !NON_ENROLLED_PROGRAMMES.has(currentProgramme);

// PCRS patient-details fields the CDM review timing/eligibility decision genuinely
// depends on. When cdm-ws returns a details object but omits any of these, the
// review must not proceed (we cannot make the mid-month / reimbursement call) —
// so we block and surface which fields are missing rather than silently treating
// the patient as "not mid-month".
//
// Kept intentionally to `registrationDate` only for now — it is the mid-month
// driver. Extend this list with `{ key, label }` entries if other returned detail
// fields become load-bearing for the review decision (e.g. gpGmsNumber, which
// today only feeds the non-blocking panel-transfer warning).
export const REQUIRED_PCRS_DETAIL_FIELDS = [
  { key: "registrationDate", label: "Registration date" },
];

const isBlankValue = (value) =>
  value === undefined || value === null || String(value).trim() === "";

// Returns the human-readable labels of the required detail fields that are
// absent/blank on a returned PCRS patient-details object. Caller is responsible
// for only invoking this once a details object was genuinely expected AND
// returned (see getIsMissingRequiredPcrsDetails).
export const getMissingRequiredPcrsDetails = (patientDetails) => {
  if (!patientDetails) return [];

  return REQUIRED_PCRS_DETAIL_FIELDS.filter(({ key }) =>
    isBlankValue(patientDetails[key])
  ).map(({ label }) => label);
};

// True only when a PCRS patient-details object was actually returned (details
// were expected: valid patient, content ready, and cdm-ws produced an object —
// private / no-GMS / no-credentials / errored paths never yield an object, so
// they are naturally excluded) yet a required field is missing.
//
// The generic "missing required details" block is ONLY for the case where PCRS
// returned NO error but still omitted a required field. When PCRS returns an
// error (e.g. "Patient not on GP's panel"), that error is the real cause of the
// incomplete data — we defer to the error-specific notice (e.g. the panel-
// transfer warning) instead of showing the generic missing-details block.
export const getMissingRequiredPcrsDetailsList = ({
  isContentReady,
  isPatientValid,
  patientDetails,
}) => {
  if (!isContentReady || !isPatientValid || !patientDetails) return [];

  const hasPcrsErrors = Array.isArray(patientDetails.errors)
    ? patientDetails.errors.length > 0
    : !!patientDetails.errors;
  if (hasPcrsErrors) return [];

  return getMissingRequiredPcrsDetails(patientDetails);
};

/**
 * The contract doctor's own GMS contract number, which every CDM submission is claimed under.
 *
 * A doctor whose staff record has none reaches the end of a review and fails at submit, with a
 * backend error that names neither the field nor where to fix it — which is exactly what happened
 * to Darren in the Centric demo (KIN-1936). The number is knowable before a single question is
 * answered, so the review should not start under a doctor who cannot claim it.
 *
 * Read off the staff record rather than the signed-in user: the review is claimed under the
 * *contract doctor* chosen on the form, who is often not the person filling it in.
 */
export const getMissingContractDoctorFields = (contractDoctor) => {
  if (!contractDoctor) return [];

  return isBlankValue(contractDoctor.gmsNumber) ? ["GMS Contract Number"] : [];
};

/**
 * The staff record for the contract doctor selected on a review form.
 *
 * The form holds the id as a string (it comes off a select), so the comparison is made on strings
 * at both ends rather than relying on a loose equality this codebase does not use.
 */
export const findContractDoctor = (staffMembers, contractDoctorId) => {
  if (!contractDoctorId) return null;

  return (
    (staffMembers ?? []).find(
      (staffMember) => String(staffMember?.id) === String(contractDoctorId)
    ) ?? null
  );
};

// Without a live PCRS web-service connection the CDM screen is read-only:
// adding, editing (correction), cancelling and deregistering are all disabled —
// unless ALLOW_REVIEW_ON_PCRS_ERROR lets reviews proceed despite PCRS problems.
export const getAreReviewActionsDisabled = (hasWsConnection) =>
  !hasWsConnection && !ALLOW_REVIEW_ON_PCRS_ERROR;

// A PCRS registration in the current (ongoing) month is not verifiable by PCRS
// until the 1st of next month — whether it's a fresh registration or a mid-month
// panel transfer, a review dated this month may not be paid / may be rejected
// until then. One unified warning (never blocks), driven purely by the
// registration date being in the current month.
export const getShowMidMonthNotice = ({
  isContentReady,
  isPatientValid,
  registrationDate,
  isMissingRequiredPcrsDetails,
}) =>
  isContentReady &&
  isPatientValid &&
  // A missing registration date is NOT "not mid-month" — the details are
  // incomplete, so the review is blocked (see getMissingRequiredPcrsDetailsList)
  // and the amber advice is suppressed in favour of the error notice.
  !isMissingRequiredPcrsDetails &&
  isDateInCurrentMonth(registrationDate);

// PCRS explicitly reports the patient is not on the GP's panel — a distinct,
// definitive error (independent of dates), separate from the mid-month warning.
export const getShowNotOnPanelNotice = ({
  isContentReady,
  isPatientValid,
  hasWsConnection,
  patientDetails,
}) =>
  isContentReady &&
  isPatientValid &&
  hasWsConnection &&
  isNotOnPanelError(patientDetails?.errors);
